/**
 * AppointmentsListPage - View all appointments (upcoming and past)
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Calendar,
  Plus,
  CheckCircle2,
  Clock,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';
import API from '@/utils/api';
import { toast } from 'sonner';
import { format, parseISO, isAfter, addDays } from 'date-fns';

const THEME = {
  bgGradient: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 30%, #DDD6FE 60%, #F5F3FF 100%)',
};

const AppointmentsListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDemoMode } = useDemo();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('view') || 'upcoming');

  useEffect(() => {
    fetchAppointments();
  }, [isDemoMode]);

  const fetchAppointments = async () => {
    if (isDemoMode) {
      setLoading(false);
      return;
    }

    try {
      const response = await API.get('/medical/appointment-preps');
      setAppointments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  // Filter appointments
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const upcomingAppointments = appointments
    .filter(a => {
      if (a.status === 'completed') return false;
      try {
        const date = parseISO(a.appointmentDate);
        return isAfter(date, addDays(today, -1));
      } catch {
        return false;
      }
    })
    .sort((a, b) => parseISO(a.appointmentDate) - parseISO(b.appointmentDate));

  const pastAppointments = appointments
    .filter(a => a.status === 'completed')
    .sort((a, b) => parseISO(b.appointmentDate) - parseISO(a.appointmentDate));

  const renderAppointmentCard = (appt, isPast = false) => (
    <Card 
      key={appt.id}
      className={`cursor-pointer hover:shadow-md transition-all ${
        isPast ? 'bg-slate-50/50' : 'bg-white'
      }`}
      onClick={() => navigate(`/medical/appointment/${appt.id}`)}
      data-testid={`appointment-${appt.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isPast ? 'bg-slate-100' : 'bg-indigo-100'
          }`}>
            {isPast ? (
              <CheckCircle2 className="h-5 w-5 text-slate-500" />
            ) : (
              <Calendar className="h-5 w-5 text-indigo-600" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium text-slate-800">
                {appt.doctorName || 'Appointment'}
              </h3>
              {!isPast && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    appt.status === 'ready' 
                      ? 'bg-green-100 text-green-700 border-green-200' 
                      : 'bg-amber-100 text-amber-700 border-amber-200'
                  }`}
                >
                  {appt.status === 'ready' ? 'Ready' : 'Draft'}
                </Badge>
              )}
            </div>
            
            <p className="text-sm text-slate-600">
              {appt.appointmentDate && format(parseISO(appt.appointmentDate), 'EEEE, MMMM d, yyyy')}
              {appt.appointmentTime && ` at ${appt.appointmentTime}`}
            </p>
            
            {appt.reason && (
              <p className="text-xs text-slate-500 mt-1 truncate">{appt.reason}</p>
            )}
            
            {isPast && (
              <div className="flex gap-2 mt-2">
                {appt.followUpNeeded && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                    Follow-up needed
                  </Badge>
                )}
                {appt.visitNotes && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                    Notes added
                  </Badge>
                )}
              </div>
            )}
          </div>
          
          <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: THEME.bgGradient }}>
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="appointments-list-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-indigo-100">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/medical')}
              className="p-2 -ml-2"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Appointments</h1>
              <p className="text-xs text-slate-500">Upcoming and past visits</p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/medical/prepare')}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="upcoming" className="text-sm">
              <Clock className="h-4 w-4 mr-2" />
              Upcoming ({upcomingAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="text-sm">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Past ({pastAppointments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {upcomingAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white/50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="font-medium text-slate-800 mb-2">No upcoming appointments</h3>
                <p className="text-sm text-slate-500 mb-4">Prepare for your next doctor visit</p>
                <Button 
                  onClick={() => navigate('/medical/prepare')}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Prepare for Appointment
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingAppointments.map(appt => renderAppointmentCard(appt))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white/50 rounded-lg">
                <p className="text-sm text-slate-500">No past appointments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pastAppointments.map(appt => renderAppointmentCard(appt, true))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AppointmentsListPage;
